import {ChevronDown, Filter} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {type ReportParameter, type ReportQuery} from '@/api/reporting';
import {isoDateDaysAgo} from '@/components/submissions/SubmissionFilters';
import {cn} from '@/lib/utils';

// A parameter's declared default as the date input shows it: the service's day offsets (`-30d`,
// `0d`) and month offsets (`-5m`, the first of the month that many months back) resolved against
// today, an ISO date as it is. Local dates, as isoDateDaysAgo is, since the analyst reads them back.
export function resolveParameterDefault(value: string | undefined): string {
  const days = /^(-?\d+)d$/.exec(value ?? '');
  if (days) {
    return isoDateDaysAgo(-Number(days[1]));
  }

  const months = /^(-?\d+)m$/.exec(value ?? '');
  if (months) {
    const first = new Date();
    first.setDate(1);
    first.setMonth(first.getMonth() + Number(months[1]));
    return `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, '0')}-01`;
  }

  return value ?? '';
}

function startsAt(parameter: ReportParameter): string {
  switch (parameter.type) {
    case 'date': {
      return resolveParameterDefault(parameter.default);
    }

    case 'toggle': {
      return parameter.default ?? parameter.options?.[0]?.value ?? '';
    }

    case 'select':
    case 'multiselect': {
      // A select or multiselect starts at all of its choices, the empty value.
      return '';
    }
  }
}

/**
 Every parameter the page shows, at its default, as the inputs show it before anything is changed.
 A hidden one is the report's own and is never sent, so it is not here.
 */
export function parameterDefaults(parameters: readonly ReportParameter[]): ReportQuery {
  return Object.fromEntries(
    parameters.filter((parameter) => !parameter.hidden).map((p) => [p.name, startsAt(p)])
  );
}

/** A multiselect's value is its choices joined by commas; none means all. */
export function selectedValues(value: string | undefined): string[] {
  return (value ?? '').split(',').filter((part) => part !== '');
}

const PRESETS: ReadonlyArray<{label: string; days: number}> = [
  {label: 'Last 7 days', days: 7},
  {label: 'Last 30 days', days: 30},
  {label: 'Last 90 days', days: 90}
];

function presetRange(days: number): {from: string; to: string} {
  return {from: isoDateDaysAgo(days), to: isoDateDaysAgo(0)};
}

// A select parameter: "All" first (the empty value), then the report's choices as it listed them. A
// value the URL carries that the choices do not offer - before they have loaded, or one that has
// since gone - is shown rather than silently dropped.
function SelectInput({
  id,
  parameter,
  value,
  onChange
}: {
  readonly id: string;
  readonly parameter: ReportParameter;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  const options = parameter.options ?? [];
  const known = value === '' || options.some((option) => option.value === value);
  return (
    <NativeSelect
      id={id}
      value={value}
      className="w-48"
      onChange={(event) => {
        onChange(event.target.value);
      }}>
      <NativeSelectOption value="">All</NativeSelectOption>
      {known ? null : <NativeSelectOption value={value}>{value}</NativeSelectOption>}
      {options.map((option) => (
        <NativeSelectOption key={option.value} value={option.value}>
          {option.label}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}

// Exactly one of a few fixed options, as the dashboard's Actor | Content switch is. Base UI's
// ToggleGroup carries its value as an array even in single-select mode; an empty `next` is the
// analyst re-clicking the pressed option, which keeps it rather than leaving neither pressed.
function ToggleInput({
  parameter,
  value,
  onChange
}: {
  readonly parameter: ReportParameter;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <ToggleGroup
      variant="outline"
      spacing={0}
      size="sm"
      aria-label={parameter.label ?? parameter.name}
      value={[value]}
      onValueChange={(next) => {
        const [first] = next;
        if (first) {
          onChange(first);
        }
      }}>
      {(parameter.options ?? []).map((option) => (
        <ToggleGroupItem key={option.value} value={option.value}>
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

// Any number of the report's choices, as the dashboard's vertical filter is: "All" ticked when none
// is, and the trigger tinted once a filter is on. Base UI checkbox items leave the menu open after a
// pick, so several can be ticked in one visit. A value the choices do not offer (before they have
// loaded, or one that has since gone) stays ticked rather than being dropped.
function MultiSelectInput({
  parameter,
  value,
  onChange
}: {
  readonly parameter: ReportParameter;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  const options = parameter.options ?? [];
  const chosen = selectedValues(value);
  const noun = (parameter.label ?? parameter.name).toLowerCase();
  const labelOf = (v: string) => options.find((option) => option.value === v)?.label ?? v;
  const [only] = chosen;
  const summary =
    chosen.length === 0
      ? `All ${noun}`
      : chosen.length === 1 && only !== undefined
        ? labelOf(only)
        : `${chosen.length} ${noun}`;
  const offered = new Set(options.map((option) => option.value));
  const extra = chosen.filter((v) => !offered.has(v));

  const toggle = (v: string) => {
    const next = chosen.includes(v) ? chosen.filter((c) => c !== v) : [...chosen, v];
    onChange(next.join(','));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Filter by ${noun}, ${summary.toLowerCase()}`}
        render={
          <Button
            variant="secondary"
            size="sm"
            className={cn(chosen.length > 0 && 'ring-primary ring-1 ring-inset')}
          />
        }>
        <Filter />
        <span className="max-w-40 truncate">{summary}</span>
        <ChevronDown />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuGroup>
          <DropdownMenuLabel>{parameter.label ?? parameter.name}</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={chosen.length === 0}
            onCheckedChange={() => {
              onChange('');
            }}>
            All {noun}
          </DropdownMenuCheckboxItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {[...options.map((o) => o.value), ...extra].map((v) => (
            <DropdownMenuCheckboxItem
              key={v}
              checked={chosen.includes(v)}
              onCheckedChange={() => {
                toggle(v);
              }}>
              {labelOf(v)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// One input per declared parameter - a date picker, or a select for a parameter whose choices the
// report's own query offers (a vertical, a content type) - driven by the report's own definition so
// a new report with different parameters needs no page change. The presets appear only for the `from`/`to` pair the
// date-ranged reports share, in the same shape as the Weekly QA range picker. Editing only changes
// `values`; nothing runs until the page's Re-run report button is clicked, because every run is a
// BigQuery query. `stacked` puts the presets above the dates; `idPrefix` keeps the inputs' ids
// unique when several parameter sets share a page.
export function ReportParameters({
  parameters,
  values,
  onChange,
  idPrefix = 'report-parameter',
  stacked = false
}: {
  readonly parameters: readonly ReportParameter[];
  readonly values: ReportQuery;
  readonly onChange: (next: ReportQuery) => void;
  readonly idPrefix?: string;
  readonly stacked?: boolean;
}) {
  const shown = parameters.filter((parameter) => !parameter.hidden);
  // The toggle and the multi-selects are filters on what the page shows, as the dashboard's are, so
  // they sit together at the start of the bar; the dates and plain selects follow, to the right.
  const filters = shown.filter((p) => p.type === 'toggle' || p.type === 'multiselect');
  const inputs = shown.filter((p) => p.type === 'date' || p.type === 'select');
  if (filters.length === 0 && inputs.length === 0) {
    return null;
  }

  const names = new Set(inputs.filter((p) => p.type === 'date').map((p) => p.name));
  const hasRange = names.has('from') && names.has('to');

  return (
    <div className={stacked ? 'flex flex-col items-start gap-3' : 'flex flex-wrap items-end gap-2'}>
      {filters.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((parameter) =>
            parameter.type === 'toggle' ? (
              <ToggleInput
                key={parameter.name}
                parameter={parameter}
                value={values[parameter.name] ?? ''}
                onChange={(value) => {
                  onChange({...values, [parameter.name]: value});
                }}
              />
            ) : (
              <MultiSelectInput
                key={parameter.name}
                parameter={parameter}
                value={values[parameter.name] ?? ''}
                onChange={(value) => {
                  onChange({...values, [parameter.name]: value});
                }}
              />
            )
          )}
        </div>
      ) : null}
      {hasRange ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {PRESETS.map((preset) => {
            const range = presetRange(preset.days);
            const active = values.from === range.from && values.to === range.to;
            return (
              <Button
                key={preset.label}
                type="button"
                variant="secondary"
                size="sm"
                aria-pressed={active}
                className="aria-pressed:bg-muted aria-pressed:text-foreground"
                onClick={() => {
                  onChange({...values, ...range});
                }}>
                {preset.label}
              </Button>
            );
          })}
        </div>
      ) : null}
      <div
        className={
          stacked ? 'flex flex-wrap items-end gap-2' : 'flex flex-wrap items-end gap-2 sm:ml-auto'
        }>
        {inputs.map((parameter) => {
          const id = `${idPrefix}-${parameter.name}`;
          if (parameter.type === 'select') {
            return (
              <Field key={parameter.name} className="w-auto">
                <FieldLabel htmlFor={id}>{parameter.label ?? parameter.name}</FieldLabel>
                <SelectInput
                  id={id}
                  parameter={parameter}
                  value={values[parameter.name] ?? ''}
                  onChange={(value) => {
                    onChange({...values, [parameter.name]: value});
                  }}
                />
              </Field>
            );
          }

          return (
            <Field key={parameter.name} className="w-auto">
              <FieldLabel htmlFor={id}>{parameter.label ?? parameter.name}</FieldLabel>
              <Input
                id={id}
                type="date"
                value={values[parameter.name] ?? ''}
                className="w-40"
                onChange={(event) => {
                  onChange({...values, [parameter.name]: event.target.value});
                }}
              />
            </Field>
          );
        })}
      </div>
    </div>
  );
}
